'use client'

import { forwardRef, useState, useEffect, useCallback, useMemo } from 'react'
import { TrendingUp, Target, Calendar, DollarSign, Users, Award } from 'lucide-react'

/**
 * BIRHAUS Progress Component
 * 
 * Enhanced progress indicator implementing all 10 BIRHAUS principles:
 * - Spanish-first labels and milestone messages
 * - Progressive disclosure of detailed metrics
 * - Visual feedback with color coding and animations
 * - Accessibility compliance (WCAG AA+)
 * - Multiple progress types for different contexts
 * - IPU Admin specific presets for campaigns and goals
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface ProgressMilestone {
  id: string
  value: number // percentage or absolute value
  labelEs: string
  labelEn: string
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  icon?: React.ReactNode
  color?: string
  reached?: boolean
}

export interface ProgressMetrics {
  current: number
  target: number
  percentage: number
  remaining: number
  timeElapsed?: number // in days
  timeRemaining?: number // in days
  averagePerDay?: number
}

export interface BirhausProgressProps {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // BIRHAUS Principle #4: Progressive disclosure
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // Progress data
  current: number
  target: number
  unit?: string // e.g., '₲', '$', 'personas', 'eventos'
  
  // Progress type
  type?: 'linear' | 'circular' | 'stepped' | 'radial'
  variant?: 'default' | 'success' | 'warning' | 'error' | 'gradient'
  
  // Milestones
  milestones?: ProgressMilestone[]
  showMilestones?: boolean
  
  // Time tracking
  startDate?: Date
  endDate?: Date
  showTimeMetrics?: boolean
  
  // Visual options
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showPercentage?: boolean
  showValues?: boolean
  showAnimation?: boolean
  animationDuration?: number // in ms
  
  // Color thresholds
  colorThresholds?: {
    low: number // e.g., 25% = red
    medium: number // e.g., 75% = yellow
    high: number // e.g., 90% = green
  }
  
  // BIRHAUS Principle #6: Accessibility = dignity
  ariaLabel?: string
  ariaLabelEs?: string
  ariaLabelEn?: string
  
  // Locale
  locale?: 'es' | 'en'
  
  // IPU Admin specific
  progressType?: 'donation_goal' | 'membership_goal' | 'event_capacity' | 'fundraising' | 'custom'
  campaignName?: string
  
  // Callbacks
  onMilestoneReached?: (milestone: ProgressMilestone) => void
  onTargetReached?: () => void
}

export const BirhausProgress = forwardRef<HTMLDivElement, BirhausProgressProps>(({
  // Labels (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // Description
  description,
  descriptionEs,
  descriptionEn,
  
  // Progress data
  current,
  target,
  unit = '',
  
  // Appearance
  type = 'linear',
  variant = 'default',
  
  // Milestones
  milestones = [],
  showMilestones = true,
  
  // Time tracking
  startDate,
  endDate,
  showTimeMetrics = false,
  
  // Visual options
  size = 'md',
  showPercentage = true,
  showValues = true,
  showAnimation = true,
  animationDuration = 1000,
  
  // Color thresholds
  colorThresholds = {
    low: 25,
    medium: 75,
    high: 90
  },
  
  // Accessibility
  ariaLabel,
  ariaLabelEs,
  ariaLabelEn,
  
  // Locale
  locale = 'es',
  
  // IPU Admin
  progressType = 'custom',
  campaignName,
  
  // Callbacks
  onMilestoneReached,
  onTargetReached
}, ref) => {
  // State for animations
  const [animatedProgress, setAnimatedProgress] = useState(0)
  const [reachedMilestones, setReachedMilestones] = useState<string[]>([])
  const [targetReached, setTargetReached] = useState(false)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }, [locale])
  
  const displayLabel = getDisplayText(label, labelEs, labelEn, '')
  const displayDescription = getDisplayText(description, descriptionEs, descriptionEn, '')
  const displayAriaLabel = getDisplayText(ariaLabel, ariaLabelEs, ariaLabelEn, 'Progress indicator')
  
  // Calculate progress metrics
  const metrics = useMemo((): ProgressMetrics => {
    const percentage = Math.min((current / target) * 100, 100)
    const remaining = Math.max(target - current, 0)
    
    let timeElapsed: number | undefined
    let timeRemaining: number | undefined
    let averagePerDay: number | undefined
    
    if (startDate) {
      const now = new Date()
      timeElapsed = Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
      
      if (endDate) {
        timeRemaining = Math.floor((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      }
      
      if (timeElapsed > 0) {
        averagePerDay = current / timeElapsed
      }
    }
    
    return {
      current,
      target,
      percentage,
      remaining,
      timeElapsed,
      timeRemaining,
      averagePerDay
    }
  }, [current, target, startDate, endDate])
  
  // Get default milestones for IPU Admin
  const getDefaultMilestones = useCallback((): ProgressMilestone[] => {
    const milestoneConfigs: Record<string, ProgressMilestone[]> = {
      donation_goal: [
        {
          id: 'quarter',
          value: 25,
          labelEs: '¼ del objetivo',
          labelEn: '¼ of goal',
          descriptionEs: 'Primer cuarto alcanzado',
          descriptionEn: 'First quarter reached',
          icon: <TrendingUp className="h-4 w-4" />,
          color: 'blue'
        },
        {
          id: 'half',
          value: 50,
          labelEs: 'Mitad del objetivo',
          labelEn: 'Half of goal',
          descriptionEs: '¡Vamos por la mitad!',
          descriptionEn: 'Halfway there!',
          icon: <Target className="h-4 w-4" />,
          color: 'yellow'
        },
        {
          id: 'three_quarters',
          value: 75,
          labelEs: '¾ del objetivo',
          labelEn: '¾ of goal',
          descriptionEs: 'Muy cerca del objetivo',
          descriptionEn: 'Very close to goal',
          icon: <TrendingUp className="h-4 w-4" />,
          color: 'orange'
        },
        {
          id: 'complete',
          value: 100,
          labelEs: 'Objetivo alcanzado',
          labelEn: 'Goal reached',
          descriptionEs: '¡Felicitaciones!',
          descriptionEn: 'Congratulations!',
          icon: <Award className="h-4 w-4" />,
          color: 'green'
        }
      ],
      membership_goal: [
        {
          id: 'first_milestone',
          value: 30,
          labelEs: 'Primeros miembros',
          labelEn: 'First members',
          icon: <Users className="h-4 w-4" />,
          color: 'blue'
        },
        {
          id: 'half_goal',
          value: 50,
          labelEs: 'Mitad del objetivo',
          labelEn: 'Half of goal',
          icon: <Users className="h-4 w-4" />,
          color: 'yellow'
        },
        {
          id: 'complete',
          value: 100,
          labelEs: 'Meta de membresía alcanzada',
          labelEn: 'Membership goal reached',
          icon: <Award className="h-4 w-4" />,
          color: 'green'
        }
      ],
      event_capacity: [
        {
          id: 'early_registration',
          value: 25,
          labelEs: 'Registro temprano',
          labelEn: 'Early registration',
          icon: <Calendar className="h-4 w-4" />,
          color: 'blue'
        },
        {
          id: 'half_capacity',
          value: 50,
          labelEs: 'Media capacidad',
          labelEn: 'Half capacity',
          icon: <Users className="h-4 w-4" />,
          color: 'yellow'
        },
        {
          id: 'near_full',
          value: 80,
          labelEs: 'Casi lleno',
          labelEn: 'Nearly full',
          icon: <TrendingUp className="h-4 w-4" />,
          color: 'orange'
        },
        {
          id: 'full_capacity',
          value: 100,
          labelEs: 'Capacidad completa',
          labelEn: 'Full capacity',
          icon: <Award className="h-4 w-4" />,
          color: 'green'
        }
      ]
    }
    
    return milestoneConfigs[progressType] || []
  }, [progressType])
  
  const allMilestones = milestones.length > 0 ? milestones : getDefaultMilestones()
  
  // Determine progress color based on percentage
  const getProgressColor = useCallback(() => {
    if (variant !== 'default') return variant
    
    const { percentage } = metrics
    
    if (percentage >= colorThresholds.high) return 'success'
    if (percentage >= colorThresholds.medium) return 'warning'
    if (percentage >= colorThresholds.low) return 'error'
    return 'error'
  }, [metrics, variant, colorThresholds])
  
  const progressColor = getProgressColor()
  
  // Format numbers for display
  const formatNumber = useCallback((num: number): string => {
    if (unit === '₲' || unit === '$') {
      return new Intl.NumberFormat(locale === 'es' ? 'es-PY' : 'en-US').format(num)
    }
    return num.toString()
  }, [unit, locale])
  
  // Format time remaining
  const formatTimeRemaining = useCallback((days: number): string => {
    if (days <= 0) {
      return locale === 'es' ? 'Tiempo vencido' : 'Time expired'
    }
    
    if (days === 1) {
      return locale === 'es' ? '1 día restante' : '1 day remaining'
    }
    
    if (days < 30) {
      return locale === 'es' ? `${days} días restantes` : `${days} days remaining`
    }
    
    const months = Math.floor(days / 30)
    const remainingDays = days % 30
    
    if (months === 1 && remainingDays === 0) {
      return locale === 'es' ? '1 mes restante' : '1 month remaining'
    }
    
    if (remainingDays === 0) {
      return locale === 'es' ? `${months} meses restantes` : `${months} months remaining`
    }
    
    return locale === 'es' 
      ? `${months} meses y ${remainingDays} días restantes`
      : `${months} months and ${remainingDays} days remaining`
  }, [locale])
  
  // Animation effect
  useEffect(() => {
    if (!showAnimation) {
      setAnimatedProgress(metrics.percentage)
      return
    }
    
    const startTime = Date.now()
    const startProgress = animatedProgress
    const targetProgress = metrics.percentage
    
    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / animationDuration, 1)
      
      // Easing function
      const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
      const currentProgress = startProgress + (targetProgress - startProgress) * easeOutCubic(progress)
      
      setAnimatedProgress(currentProgress)
      
      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }
    
    requestAnimationFrame(animate)
  }, [metrics.percentage, showAnimation, animationDuration])
  
  // Milestone and target reached effects
  useEffect(() => {
    // Check milestones
    const newReachedMilestones = allMilestones
      .filter(milestone => metrics.percentage >= milestone.value)
      .map(milestone => milestone.id)
    
    const newlyReached = newReachedMilestones.filter(id => !reachedMilestones.includes(id))
    
    if (newlyReached.length > 0) {
      setReachedMilestones(newReachedMilestones)
      
      // Call milestone callback for newly reached milestones
      newlyReached.forEach(milestoneId => {
        const milestone = allMilestones.find(m => m.id === milestoneId)
        if (milestone && onMilestoneReached) {
          onMilestoneReached(milestone)
        }
      })
    }
    
    // Check target reached
    if (metrics.percentage >= 100 && !targetReached) {
      setTargetReached(true)
      onTargetReached?.()
    }
  }, [metrics.percentage, allMilestones, reachedMilestones, targetReached, onMilestoneReached, onTargetReached])
  
  // Style classes
  const sizeClasses = {
    sm: {
      container: 'p-3',
      bar: 'h-2',
      circle: 'w-16 h-16',
      text: 'text-sm',
      label: 'text-xs'
    },
    md: {
      container: 'p-4',
      bar: 'h-3',
      circle: 'w-24 h-24',
      text: 'text-base',
      label: 'text-sm'
    },
    lg: {
      container: 'p-6',
      bar: 'h-4',
      circle: 'w-32 h-32',
      text: 'text-lg',
      label: 'text-base'
    },
    xl: {
      container: 'p-8',
      bar: 'h-6',
      circle: 'w-40 h-40',
      text: 'text-xl',
      label: 'text-lg'
    }
  }
  
  const colorClasses = {
    default: 'bg-gray-200',
    success: 'bg-green-500',
    warning: 'bg-yellow-500',
    error: 'bg-red-500',
    gradient: 'bg-gradient-to-r from-blue-500 to-green-500'
  }
  
  const currentSizeClasses = sizeClasses[size]
  
  // Render linear progress bar
  const renderLinearProgress = useCallback(() => (
    <div className="w-full">
      <div className={cn('bg-gray-200 rounded-full overflow-hidden', currentSizeClasses.bar)}>
        <div
          className={cn(
            'h-full transition-all duration-300 ease-out rounded-full',
            colorClasses[progressColor]
          )}
          style={{ width: `${Math.min(animatedProgress, 100)}%` }}
          role="progressbar"
          aria-valuenow={Math.round(animatedProgress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={displayAriaLabel}
        />
      </div>
      
      {/* Milestones */}
      {showMilestones && allMilestones.length > 0 && (
        <div className="relative mt-2">
          {allMilestones.map((milestone) => (
            <div
              key={milestone.id}
              className="absolute transform -translate-x-1/2"
              style={{ left: `${milestone.value}%` }}
            >
              <div className={cn(
                'w-2 h-2 rounded-full border-2 bg-white',
                reachedMilestones.includes(milestone.id)
                  ? 'border-green-500'
                  : 'border-gray-300'
              )} />
              
              {milestone.value <= animatedProgress && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                  <div className={cn(
                    'text-center',
                    currentSizeClasses.label,
                    'text-gray-600'
                  )}>
                    {getDisplayText(
                      undefined,
                      milestone.labelEs,
                      milestone.labelEn,
                      milestone.id
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  ), [
    currentSizeClasses,
    colorClasses,
    progressColor,
    animatedProgress,
    displayAriaLabel,
    showMilestones,
    allMilestones,
    reachedMilestones,
    getDisplayText
  ])
  
  // Render circular progress
  const renderCircularProgress = useCallback(() => {
    const radius = size === 'sm' ? 28 : size === 'lg' ? 58 : size === 'xl' ? 74 : 44
    const circumference = 2 * Math.PI * radius
    const strokeDashoffset = circumference - (animatedProgress / 100) * circumference
    
    return (
      <div className="relative inline-flex items-center justify-center">
        <svg className={currentSizeClasses.circle} viewBox="0 0 120 120">
          {/* Background circle */}
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="8"
          />
          {/* Progress circle */}
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 60 60)"
            className={cn(
              'transition-all duration-300 ease-out',
              progressColor === 'success' ? 'text-green-500' :
              progressColor === 'warning' ? 'text-yellow-500' :
              progressColor === 'error' ? 'text-red-500' :
              'text-blue-500'
            )}
          />
        </svg>
        
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {showPercentage && (
            <div className={cn('font-bold', currentSizeClasses.text)}>
              {Math.round(animatedProgress)}%
            </div>
          )}
          {showValues && (
            <div className={cn('text-gray-600', currentSizeClasses.label)}>
              {formatNumber(current)} / {formatNumber(target)}
            </div>
          )}
        </div>
      </div>
    )
  }, [
    size,
    currentSizeClasses,
    animatedProgress,
    progressColor,
    showPercentage,
    showValues,
    formatNumber,
    current,
    target
  ])
  
  return (
    <div ref={ref} className={cn('birhaus-progress', currentSizeClasses.container)}>
      {/* Header */}
      <div className="mb-4">
        {/* Title */}
        {(displayLabel || campaignName) && (
          <div className="flex items-center justify-between mb-2">
            <h3 className={cn('font-semibold', currentSizeClasses.text)}>
              {displayLabel || campaignName}
            </h3>
            
            {showPercentage && type === 'linear' && (
              <span className={cn('font-medium', currentSizeClasses.text)}>
                {Math.round(animatedProgress)}%
              </span>
            )}
          </div>
        )}
        
        {/* Description */}
        {displayDescription && (
          <p className={cn('text-gray-600', currentSizeClasses.label)}>
            {displayDescription}
          </p>
        )}
        
        {/* Values */}
        {showValues && type === 'linear' && (
          <div className="flex items-center justify-between mt-2">
            <span className={cn('text-gray-600', currentSizeClasses.label)}>
              {unit}{formatNumber(current)} / {unit}{formatNumber(target)}
            </span>
            
            {metrics.remaining > 0 && (
              <span className={cn('text-gray-500', currentSizeClasses.label)}>
                {locale === 'es' ? 'Faltan' : 'Remaining'}: {unit}{formatNumber(metrics.remaining)}
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Progress Indicator */}
      <div className="mb-4">
        {type === 'circular' ? renderCircularProgress() : renderLinearProgress()}
      </div>
      
      {/* Time Metrics */}
      {showTimeMetrics && (metrics.timeRemaining !== undefined || metrics.averagePerDay !== undefined) && (
        <div className="space-y-2">
          {metrics.timeRemaining !== undefined && (
            <div className={cn('text-gray-600', currentSizeClasses.label)}>
              <Calendar className="inline h-4 w-4 mr-1" />
              {formatTimeRemaining(metrics.timeRemaining)}
            </div>
          )}
          
          {metrics.averagePerDay !== undefined && metrics.averagePerDay > 0 && (
            <div className={cn('text-gray-600', currentSizeClasses.label)}>
              <TrendingUp className="inline h-4 w-4 mr-1" />
              {locale === 'es' ? 'Promedio diario' : 'Daily average'}: {unit}{formatNumber(Math.round(metrics.averagePerDay))}
            </div>
          )}
        </div>
      )}
      
      {/* Reached Milestones */}
      {showMilestones && reachedMilestones.length > 0 && (
        <div className="mt-4">
          <h4 className={cn('font-medium mb-2', currentSizeClasses.label)}>
            {locale === 'es' ? 'Hitos alcanzados' : 'Milestones reached'}
          </h4>
          <div className="space-y-1">
            {allMilestones
              .filter(milestone => reachedMilestones.includes(milestone.id))
              .map(milestone => (
                <div key={milestone.id} className="flex items-center space-x-2">
                  {milestone.icon && (
                    <span className="text-green-500">{milestone.icon}</span>
                  )}
                  <span className={cn('text-green-700', currentSizeClasses.label)}>
                    {getDisplayText(
                      undefined,
                      milestone.labelEs,
                      milestone.labelEn,
                      milestone.id
                    )}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
      
      {/* Target Reached Celebration */}
      {targetReached && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center space-x-2 text-green-700">
            <Award className="h-5 w-5" />
            <span className={cn('font-medium', currentSizeClasses.text)}>
              {locale === 'es' ? '¡Objetivo alcanzado!' : 'Goal reached!'}
            </span>
          </div>
        </div>
      )}
    </div>
  )
})

BirhausProgress.displayName = 'BirhausProgress'

// Hook for enhanced progress functionality
export const useBirhausProgress = (target: number, initialValue: number = 0) => {
  const [current, setCurrent] = useState(initialValue)
  const [milestones, setMilestones] = useState<ProgressMilestone[]>([])
  
  const increment = useCallback((amount: number) => {
    setCurrent(prev => Math.min(prev + amount, target))
  }, [target])
  
  const decrement = useCallback((amount: number) => {
    setCurrent(prev => Math.max(prev - amount, 0))
  }, [])
  
  const setProgress = useCallback((value: number) => {
    setCurrent(Math.max(0, Math.min(value, target)))
  }, [target])
  
  const reset = useCallback(() => {
    setCurrent(0)
  }, [])
  
  const percentage = Math.min((current / target) * 100, 100)
  const remaining = Math.max(target - current, 0)
  const isComplete = current >= target
  
  return {
    current,
    setCurrent: setProgress,
    increment,
    decrement,
    reset,
    percentage,
    remaining,
    isComplete,
    milestones,
    setMilestones
  }
}

// IPU Admin specific progress configurations
export const ipuAdminProgressPresets = {
  donationGoal: (target: number, currency: string = '₲') => ({
    type: 'linear' as const,
    progressType: 'donation_goal' as const,
    unit: currency,
    showTimeMetrics: true,
    showMilestones: true,
    colorThresholds: {
      low: 30,
      medium: 70,
      high: 90
    }
  }),
  
  membershipGoal: (target: number, locale: string = 'es') => ({
    type: 'circular' as const,
    progressType: 'membership_goal' as const,
    unit: locale === 'es' ? ' personas' : ' people',
    showMilestones: true,
    colorThresholds: {
      low: 25,
      medium: 60,
      high: 85
    }
  }),
  
  eventCapacity: (target: number, locale: string = 'es') => ({
    type: 'linear' as const,
    progressType: 'event_capacity' as const,
    unit: locale === 'es' ? ' asistentes' : ' attendees',
    showMilestones: true,
    showTimeMetrics: false,
    colorThresholds: {
      low: 20,
      medium: 50,
      high: 80
    }
  })
}